/**
 * IrrigationPlanPage - Refactored main component
 * Only handles routing, tab switching, and orchestration
 * Reduced from 843 lines to ~300 lines
 */

import { useAtomValue, useSetAtom } from "jotai";
import { MapIcon, Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "wouter";
import { Tabs } from "@/components";
import { TimelineShell } from "@/components/ui/TimelineShell";
import { ensureValidPlanSchedule, type PlanScheduleLike } from "@/pages/main/utils/irrigationPlanOverlap";
import { appShell, hasActiveLoadingAtom } from "@/store";
import {
  anyProjectRunningAtom,
  irrigationPlanByIdAtom,
  irrigationPlanHistoryErrorAtom,
  irrigationPlanHistorySelectionReadyAtom,
  irrigationPlanHistoryTimelineAtom,
  projectByIdAtom,
  propertiesAtom,
  sectorsByProjectIdAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import { appConfig } from "@/utils/config";
import { DiscardChangesModal } from "./IrrigationPlanPage/components/DiscardChangesModal";
import { IrrigationPlanConfig } from "./IrrigationPlanPage/components/IrrigationPlanConfig";
import { IrrigationPlanHeader } from "./IrrigationPlanPage/components/IrrigationPlanHeader";
import { IrrigationPlanHistory } from "./IrrigationPlanPage/components/IrrigationPlanHistory";
import { IrrigationPlanSectors } from "./IrrigationPlanPage/components/IrrigationPlanSectors";
import { useIrrigationPlanForm } from "./IrrigationPlanPage/hooks/useIrrigationPlanForm";
import { useIrrigationPlanHistory } from "./IrrigationPlanPage/hooks/useIrrigationPlanHistory";
import { useIrrigationPlanSteps } from "./IrrigationPlanPage/hooks/useIrrigationPlanSteps";
import { HistoryFiltersBar, HistorySummaryCards, TimelineEntryCard } from "./reports/components";

// Define tabs configuration with full type safety
const IRRIGATION_PLAN_TABS = [
  { key: "config", label: "Config.", icon: Settings },
  { key: "sectors", label: "Setores", icon: MapIcon },
] as const;

// Add history tab for edit mode
const IRRIGATION_PLAN_TABS_WITH_HISTORY = [
  ...IRRIGATION_PLAN_TABS,
  { key: "history", label: "Histórico", icon: MapIcon }, // Using MapIcon as placeholder
] as const;

type TabType = (typeof IRRIGATION_PLAN_TABS)[number]["key"];

type IrrigationPlanDetailPageProps = {
  planId: string;
  activeTab: string;
};

function IrrigationPlanPage({ planId, activeTab: initialActiveTab }: IrrigationPlanDetailPageProps) {
  const [searchParams] = useSearchParams();
  const [, setLocation] = useLocation();

  // Validate and normalize active tab
  const tabs = planId === "new" ? IRRIGATION_PLAN_TABS : IRRIGATION_PLAN_TABS_WITH_HISTORY;
  if (initialActiveTab && !tabs.some((tab) => tab.key === initialActiveTab)) {
    initialActiveTab = "config"; // Default to config if invalid
  }
  const [activeTab, setActiveTab] = useState<string>(initialActiveTab);

  // Determine mode
  const isCreateMode = planId === "new";

  // Data atoms
  const irrigationPlanById = useAtomValue(irrigationPlanByIdAtom);
  const projectById = useAtomValue(projectByIdAtom);
  const sectorsByProjectId = useAtomValue(sectorsByProjectIdAtom);
  const properties = useAtomValue(propertiesAtom);
  const loading = useAtomValue(hasActiveLoadingAtom);
  const anyProjectRunning = useAtomValue(anyProjectRunningAtom);
  const waterPumpVirtualDeviceConfigMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);

  // Get plan and project data
  const plan = useMemo(() => {
    return isCreateMode ? null : irrigationPlanById(planId);
  }, [irrigationPlanById, planId, isCreateMode]);

  const projectId =
    useMemo(() => {
      if (isCreateMode) {
        return searchParams.get("projectId");
      }
      return plan?.project as string | undefined;
    }, [isCreateMode, searchParams, plan]) ?? null;

  // Back button setup (after projectId is resolved)
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  useEffect(() => {
    const backPath = projectId ? `/app/projects/${projectId}` : "/app/irrigation-plans";
    setBackButton(backPath);
    return () => {
      setBackButton(false);
    };
  }, [setBackButton, projectId]);

  const project = useMemo(() => {
    return projectId ? projectById(projectId) : null;
  }, [projectById, projectId]);

  const sectors = useMemo(() => {
    return projectId ? (sectorsByProjectId(projectId) ?? []) : [];
  }, [projectId, sectorsByProjectId]);

  // Frequency inverter check
  const hasFrequencyInverter = useMemo(() => {
    if (!project || !project.irrigation_water_pump) return false;

    const irrigationPump = properties
      .flatMap((p) => p.water_pumps)
      .find((pump) => pump.id === project.irrigation_water_pump);

    return irrigationPump ? waterPumpVirtualDeviceConfigMap.get(irrigationPump.id)?.mode === "analog" : false;
  }, [project, properties, waterPumpVirtualDeviceConfigMap]);

  // Other plans for validation
  // Minimum gap depends on schedule resumption; see docs/adr/0001-minimum-plan-gap-by-schedule-resumption.md
  const gapConfig = appConfig.irrigation.planMinimumGapMinutes;

  const scheduleResumptionEnabled = useMemo(() => {
    if (!project?.localized_irrigation_controller) {
      // No LIC → default to "enabled" (safe/conservative)
      return true;
    }

    const licId = project.localized_irrigation_controller;
    const licPropertyDevice = properties.flatMap((p) => p.devices).find((pd) => pd.device?.id === licId);

    const value = licPropertyDevice?.metadata?.enable_schedule_resumption;
    if (value === true || value === false) {
      return value;
    }

    // Metadata missing or flag not set → default to "enabled" (safe/conservative)
    return true;
  }, [project, properties]);

  const effectiveMinimumGapMinutes = useMemo(() => {
    return scheduleResumptionEnabled ? gapConfig.withResumption : gapConfig.withoutResumption;
  }, [scheduleResumptionEnabled, gapConfig]);

  const projectPlans = project?.irrigation_plans ?? [];
  const currentPlanId = !isCreateMode && planId !== "new" ? planId : null;

  const otherPlanSchedules = useMemo<PlanScheduleLike[]>(() => {
    if (!projectPlans.length) {
      return [];
    }

    return projectPlans
      .filter((existingPlan) => existingPlan.id !== currentPlanId && existingPlan.is_enabled)
      .map((existingPlan) => {
        const totalDurationSeconds =
          existingPlan.total_irrigation_duration ??
          (existingPlan.steps || []).reduce((total, step) => total + (step.duration_seconds ?? 0), 0);

        const totalDurationMinutes = Math.max(0, Math.ceil((totalDurationSeconds || 0) / 60));

        return ensureValidPlanSchedule({
          id: existingPlan.id,
          name: existingPlan.name,
          daysOfWeek: existingPlan.days_of_week ?? [],
          startTime: existingPlan.start_time,
          totalDurationMinutes,
        });
      })
      .filter((schedule) => schedule.daysOfWeek.length > 0);
  }, [currentPlanId, projectPlans]);

  // Extract mirrors from plan steps (account tree loads sector_mirrors nested in steps)
  const planMirrors = useMemo(() => {
    if (!plan?.steps) return undefined;
    return plan.steps
      .flatMap((step: any) =>
        (step.sector_mirrors || []).map((mirror: any) => ({
          id: mirror.id,
          source_irrigation_plan_step: mirror.source_irrigation_plan_step,
          target_sector: mirror.target_sector,
        }))
      )
      .filter((m: any) => m.id);
  }, [plan?.steps]);

  // Custom hooks
  const planSteps = useIrrigationPlanSteps({
    planId,
    sectors,
    plan,
    mirrors: planMirrors,
  });

  const planForm = useIrrigationPlanForm({
    planId,
    isCreateMode,
    projectId,
    plan,
    otherPlanSchedules,
    minimumGapMinutes: effectiveMinimumGapMinutes,
    resumptionEnabled: scheduleResumptionEnabled,
    withoutResumptionGap: gapConfig.withoutResumption,
    totalDurationSeconds: planSteps.totalDurationSeconds,
    anyProjectRunning,
  });

  const planHistory = useIrrigationPlanHistory({
    plan,
    projectId,
    activeTab,
  });

  const historyTimelineEntries = useAtomValue(irrigationPlanHistoryTimelineAtom);
  const historyTimelineError = useAtomValue(irrigationPlanHistoryErrorAtom);
  const historySelectionReady = useAtomValue(irrigationPlanHistorySelectionReadyAtom);

  // Handler for viewing execution report
  const handleViewExecutionReport = () => {
    if (!projectId || !planId || planId === "new") return;
    setLocation(`/app/reports/irrigation-plan-execution-report?projectId=${projectId}&planId=${planId}`);
  };

  // Early returns for loading/error states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Only show "not found" error in edit mode
  if (!isCreateMode && !plan) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cronograma não encontrado</h2>
          <p className="text-gray-600">O cronograma solicitado não foi encontrado ou não existe.</p>
        </div>
      </div>
    );
  }

  // In create mode, ensure we have a project
  if (isCreateMode && !project) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Projeto não encontrado</h2>
          <p className="text-gray-600">O projeto especificado não foi encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <IrrigationPlanHeader
        plan={plan}
        isCreateMode={isCreateMode}
        planSteps={planSteps.planSteps}
        totalDuration={planSteps.totalDurationSeconds}
        project={project}
        onViewExecutionReport={handleViewExecutionReport}
      />

      {/* Tab Navigation */}
      {!isCreateMode && (
        <div className="">
          <Tabs
            tabs={tabs}
            value={activeTab as TabType}
            onChange={setActiveTab}
            className=""
            searchParamsKey="activeTab"
          />
        </div>
      )}

      {/* Tab Content */}
      <div className="pb-20">
        {activeTab === "config" && (
          <IrrigationPlanConfig
            data={planForm.planData}
            project={project || undefined}
            onChange={planForm.handlePlanDataChange}
            startValidationMessage={planForm.startValidationMessage}
            resumptionHint={planForm.resumptionHint}
            minimumGapMinutes={effectiveMinimumGapMinutes}
            readOnly={anyProjectRunning}
            canSave={planForm.canSavePlan}
            onSave={planForm.handleSave}
            onCancel={planForm.handleCancel}
            onDelete={!isCreateMode ? planForm.handleDelete : undefined}
            hasChanges={planForm.hasChanges}
            error={planForm.error}
            anyProjectRunning={anyProjectRunning}
            startValidationValid={planForm.overlapValidationValid}
          />
        )}

        {activeTab === "sectors" && projectId && (
          <IrrigationPlanSectors
            projectId={projectId}
            planId={currentPlanId ?? undefined}
            planData={planForm.planData}
            steps={planSteps.planSteps}
            sectors={sectors}
            hasFrequencyInverter={hasFrequencyInverter}
            existingPlans={otherPlanSchedules}
            minimumGapMinutes={effectiveMinimumGapMinutes}
            resumptionEnabled={scheduleResumptionEnabled}
            withoutResumptionGap={gapConfig.withoutResumption}
            onAddStep={planSteps.handleAddStep}
            onRemoveStep={planSteps.handleRemoveStep}
            onBulkRemoveSteps={planSteps.handleBulkRemoveSteps}
            onSaveStep={planSteps.handleSaveStep}
            onUpdateStep={planSteps.handleUpdateStep}
            onMoveStep={planSteps.handleMoveStep}
            onReorderSteps={planSteps.handleReorderSteps}
            mirrors={planMirrors}
            loading={loading}
            readOnly={anyProjectRunning}
            projectData={
              project
                ? {
                    pipe_wash_time_seconds: project.pipe_wash_time_seconds,
                  }
                : undefined
            }
          />
        )}

        {activeTab === "history" && !isCreateMode && (
          <IrrigationPlanHistory
            filtersBar={<HistoryFiltersBar />}
            summaryCards={<HistorySummaryCards />}
            timeline={
              <TimelineShell
                entries={historyTimelineEntries}
                getTimestamp={(entry) => entry.startTime ?? entry.endTime ?? entry.packetDate}
                getEntryKey={(entry) => entry.id}
                renderEntry={(entry) => <TimelineEntryCard entry={entry} />}
                loading={planHistory.historyLoading}
                error={historyTimelineError}
                notReady={!historySelectionReady}
                onRetry={planHistory.handleRetry}
              />
            }
            hasMore={planHistory.historyHasMore}
            isLoading={planHistory.historyLoading}
            onLoadMore={planHistory.handleLoadMore}
          />
        )}
      </div>

      <DiscardChangesModal
        isOpen={planForm.showDiscardModal}
        onClose={planForm.handleCancelDiscard}
        onConfirm={planForm.handleConfirmDiscard}
      />
    </div>
  );
}

export default IrrigationPlanPage;
